/**
 * Typed façade over the shared camera maths.
 *
 * The implementation lives in `shared/camera.js` so the server-side test suite
 * can exercise the pan/zoom rules directly; this file only adds the types the
 * browser code works with.
 */
import * as m from '@shared/camera.js';
import type { Bounds, Camera, Viewport } from './types';

export const clamp: (v: number, lo: number, hi: number) => number = m.clamp;
export const clampZoom: (zoom: number) => number = m.clampZoom;

export const visibleRect: (cam: Camera, vp: Viewport) => Bounds = m.visibleRect;

export const worldToScreen: (
  cam: Camera, vp: Viewport, wx: number, wy: number,
) => { x: number; y: number } = m.worldToScreen;

export const screenToWorld: (
  cam: Camera, vp: Viewport, sx: number, sy: number,
) => { x: number; y: number } = m.screenToWorld;

export const pannableRegion: (
  content: Bounds | null, cam: Camera, vp: Viewport,
) => Bounds = m.pannableRegion;

export const clampCamera: (cam: Camera, vp: Viewport, content: Bounds | null) => Camera = m.clampCamera;

export const zoomAt: (
  cam: Camera, vp: Viewport, sx: number, sy: number, factor: number,
) => Camera = m.zoomAt;

export const cameraForBounds: (box: Bounds, vp: Viewport, padding?: number) => Camera = m.cameraForBounds;

export const cameraForPeerViewport: (
  peer: { cx: number; cy: number; zoom: number; w: number; h: number },
  vp: Viewport,
) => Camera = m.cameraForPeerViewport;

export const easeOutCubic: (t: number) => number = m.easeOutCubic;
export const lerpCamera: (a: Camera, b: Camera, t: number) => Camera = m.lerpCamera;
export const camerasClose: (a: Camera, b: Camera, vp: Viewport) => boolean = m.camerasClose;
export const padBounds: (box: Bounds, factor: number) => Bounds = m.padBounds;

export const ZOOM_MIN: number = m.ZOOM_MIN;
export const ZOOM_MAX: number = m.ZOOM_MAX;
